import React, { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Button,
  Checkbox, FormControlLabel
} from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object({
  name: yup.string().required('Name required'),
  code: yup.string().required('Code required'),
  type: yup.string().required(),
  creditsPerCycle: yup.number().required().positive(),
});

const LeavePolicyForm = ({ open, onClose, onSave, editing }) => {
  const { register, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '', code: '', type: 'Earned', creditsPerCycle: 0,
      carryForward: { enabled: false, maxDays: 0 },
      applicability: { employmentTypes: [], genders: [] },
      restrictions: { minAdvanceNotice: 0, sandwichRule: false },
      description: ''
    }
  });

  const carryForwardEnabled = watch('carryForward.enabled');
  const applicabilityEmploymentTypes = watch('applicability.employmentTypes');
  const applicabilityGenders = watch('applicability.genders');
  const restrictionsSandwichRule = watch('restrictions.sandwichRule');

  useEffect(() => {
    if (editing) {
      reset({
        ...editing,
        applicability: editing.applicability || { employmentTypes: [], genders: [] },
        restrictions: editing.restrictions || { minAdvanceNotice: 0, sandwichRule: false },
        carryForward: editing.carryForward || { enabled: false, maxDays: 0 }
      });
    } else {
      reset();
    }
  }, [editing, reset]);

  const onSubmit = (data) => {
    onSave(data);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{editing ? 'Edit Policy' : 'Add Policy'}</DialogTitle>
        <DialogContent>
          <TextField
            {...register('name')}
            label="Name"
            fullWidth
            sx={{ mt: 1 }}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            {...register('code')}
            label="Code"
            fullWidth
            sx={{ mt: 1 }}
            error={!!errors.code}
            helperText={errors.code?.message}
          />
          <FormControl fullWidth sx={{ mt: 1 }} error={!!errors.type}>
            <InputLabel>Type</InputLabel>
            <Select {...register('type')} label="Type">
              <MenuItem value="Earned">Earned</MenuItem>
              <MenuItem value="Non-Earned">Non-Earned</MenuItem>
              <MenuItem value="Compensatory">Compensatory</MenuItem>
            </Select>
            {errors.type && <div>{errors.type.message}</div>}
          </FormControl>
          <TextField
            {...register('creditsPerCycle', { valueAsNumber: true })}
            label="Credits per Cycle"
            type="number"
            fullWidth
            sx={{ mt: 1 }}
            error={!!errors.creditsPerCycle}
            helperText={errors.creditsPerCycle?.message}
          />
          <FormControlLabel
            control={
              <Checkbox
                {...register('carryForward.enabled')}
              />
            }
            label="Carry Forward Enabled"
            sx={{ mt: 1 }}
          />
          <TextField
            {...register('carryForward.maxDays', { valueAsNumber: true })}
            label="Max Carry Forward Days"
            type="number"
            fullWidth
            sx={{ mt: 1 }}
            disabled={!carryForwardEnabled}
          />
          <TextField
            {...register('restrictions.minAdvanceNotice', { valueAsNumber: true })}
            label="Min Advance Notice (days)"
            type="number"
            fullWidth
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                {...register('restrictions.sandwichRule')}
              />
            }
            label="Sandwich Rule (Holidays/weekends between leaves count as leave)"
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Applicable Employment Types</InputLabel>
            <Select
              multiple
              {...register('applicability.employmentTypes')}
              value={applicabilityEmploymentTypes || []}
              onChange={(e) => setValue('applicability.employmentTypes', e.target.value)}
              label="Applicable Employment Types"
            >
              <MenuItem value="Full-time">Full-time</MenuItem>
              <MenuItem value="Part-time">Part-time</MenuItem>
              <MenuItem value="Contract">Contract</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Applicable Genders</InputLabel>
            <Select
              multiple
              {...register('applicability.genders')}
              value={applicabilityGenders || []}
              onChange={(e) => setValue('applicability.genders', e.target.value)}
              label="Applicable Genders"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            {...register('description')}
            label="Description"
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LeavePolicyForm;
